/**
 * Utility functions for handling UTM parameters and affiliate tracking
 */

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface TrackingParams extends UTMParams {
  aff?: string;
  __distinct_id?: string; // PostHog distinct ID for cross-domain tracking
}

/**
 * Gets the affiliate parameter from URL first, then localStorage as fallback
 * Implements "first contact wins" logic - returns the stored affiliate if it exists
 */
function getAffiliate(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  // Check localStorage first (first contact wins)
  const storedAffiliate = localStorage.getItem("aff");
  if (storedAffiliate) {
    return storedAffiliate;
  }

  // If no stored affiliate, check URL
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("aff");
}

/**
 * Extracts all tracking parameters (UTM + affiliate + PostHog distinct_id) from the current URL
 * Falls back to localStorage for affiliate if not in URL
 * This function works on the client side only
 */
export function getTrackingParams(): TrackingParams {
  if (typeof window === "undefined") {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const trackingParams: TrackingParams = {};

  // Get UTM parameters
  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ] as const;
  utmKeys.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      trackingParams[key] = value;
    }
  });

  // Get affiliate parameter (uses getAffiliate which checks localStorage first)
  const affiliate = getAffiliate();
  if (affiliate) {
    trackingParams.aff = affiliate;
  }

  // Get PostHog distinct_id for cross-domain tracking
  // @ts-ignore - PostHog is loaded globally
  if (typeof window !== "undefined" && window.posthog) {
    // @ts-ignore
    const distinctId = window.posthog.get_distinct_id();
    if (distinctId) {
      trackingParams.__distinct_id = distinctId;
    }
  }

  return trackingParams;
}

/**
 * Appends all tracking parameters (UTM + affiliate) to a given URL
 */
export function appendTrackingParams(
  baseUrl: string,
  trackingParams: TrackingParams,
): URL {
  const url = new URL(baseUrl);
  const hasAffiliate = trackingParams.aff !== undefined;
  const hasCoupon = url.searchParams.has("coupon");

  Object.entries(trackingParams).forEach(([key, value]) => {
    if (key && value) {
      url.searchParams.set(key, value);
    }
  });

  // if have both "aff" and "coupon" parameters, 
  // remove the "coupon" parameter and left only the "aff" parameter
  if (hasAffiliate && hasCoupon) {
    url.searchParams.delete("coupon");
  }

  return url;
}
